// PackageManagement.js - Admin interface voor package management
import React, { useState, useEffect } from 'react';
import { 
  activatePackage, 
  extendPackage, 
  getAllUsers, 
  getPackageStatistics, 
  deactivatePackage 
} from '../utils/adminPackageHelpers';
import './PackageManagement.css';

const PackageManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    hasActivePackage: undefined
  });

  // NEW: Selected user for modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  // Package activation form
  const [activationForm, setActivationForm] = useState({
    name: '',
    email: '',
    scansTotal: 4,
    validityDays: 365
  });

  // Package extension form
  const [extensionForm, setExtensionForm] = useState({
    userEmail: '',
    additionalScans: 0,
    additionalDays: 0
  });

  const [message, setMessage] = useState({ type: '', text: '' });

  // Load initial data
  useEffect(() => {
    loadUsers();
    loadStatistics();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const userData = await getAllUsers(filters);
      setUsers(userData);
    } catch (error) {
      showMessage('error', 'Fout bij laden gebruikers: ' + error.message);
    }
    setLoading(false);
  };

  const loadStatistics = async () => {
    try {
      console.log('🔍 Starting to load statistics...');
      const stats = await getPackageStatistics();
      console.log('📊 Statistics received:', stats);
      setStatistics(stats);
    } catch (error) {
      console.error('❌ Error loading statistics:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // NEW: Open user modal
  const openUserModal = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  // NEW: Close user modal
  const closeUserModal = () => {
    setSelectedUser(null);
    setShowUserModal(false);
  };

  // NEW: Quick add scans from modal
  const handleQuickAddScans = async (additionalScans) => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      const result = await extendPackage(
        selectedUser.email,
        additionalScans,
        0
      );
      
      if (result.success) {
        showMessage('success', result.message);
        closeUserModal();
        loadUsers();
        loadStatistics();
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Fout bij toevoegen scans: ' + error.message);
    }
    setLoading(false);
  };

  // NEW: Quick extend validity from modal
  const handleQuickExtendValidity = async (additionalDays) => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      const result = await extendPackage(
        selectedUser.email,
        0,
        additionalDays
      );
      
      if (result.success) {
        showMessage('success', result.message);
        closeUserModal();
        loadUsers();
        loadStatistics();
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Fout bij verlengen geldigheid: ' + error.message);
    }
    setLoading(false);
  };

  const handleActivatePackage = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await activatePackage({
        ...activationForm,
        activatedBy: 'admin@tractum.nl'
      });
      
      if (result.success) {
        showMessage('success', result.message);
        setActivationForm({ name: '', email: '', scansTotal: 4, validityDays: 365 });
        loadUsers();
        loadStatistics();
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Fout bij activeren scans: ' + error.message);
    }
    
    setLoading(false);
  };

  const handleExtendPackage = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await extendPackage(
        extensionForm.userEmail,
        extensionForm.additionalScans,
        extensionForm.additionalDays
      );
      
      if (result.success) {
        showMessage('success', result.message);
        setExtensionForm({ userEmail: '', additionalScans: 0, additionalDays: 0 });
        loadUsers();
        loadStatistics();
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Fout bij verlengen scans: ' + error.message);
    }
    
    setLoading(false);
  };

  const handleDeactivatePackage = async (userEmail) => {
    if (!window.confirm(`Scans deactiveren voor ${userEmail}?`)) return;
    
    setLoading(true);
    try {
      const result = await deactivatePackage(userEmail, 'Admin deactivation');
      
      if (result.success) {
        showMessage('success', result.message);
        closeUserModal();
        loadUsers();
        loadStatistics();
      } else {
        showMessage('error', result.message);
      }
    } catch (error) {
      showMessage('error', 'Fout bij deactiveren: ' + error.message);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    loadUsers();
  };

  const renderOverview = () => (
    <div className="package-overview">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{statistics.totalUsers || 0}</div>
          <div className="stat-label">Totaal Gebruikers</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.usersWithPackages || 0}</div>
          <div className="stat-label">Met Packages</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.activePackages || 0}</div>
          <div className="stat-label">Actieve Packages</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.expiredPackages || 0}</div>
          <div className="stat-label">Verlopen</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.totalScansUsed || 0}</div>
          <div className="stat-label">Scans Gebruikt</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{statistics.utilizationRate || 0}%</div>
          <div className="stat-label">Gebruik Ratio</div>
        </div>
      </div>

      {statistics.packagesExpiringSoon > 0 && (
        <div className="alert alert-warning">
          <strong>Let op:</strong> {statistics.packagesExpiringSoon} package(s) verlopen binnen 7 dagen.
        </div>
      )}
    </div>
  );

  // NEW: Verbeterd gebruikers overzicht
  const renderUsers = () => (
    <div className="user-management">
      <div className="filters-section">
        <h3>Gebruikers Overzicht</h3>
        <div className="filters">
          <input
            type="text"
            placeholder="Zoek op naam..."
            value={filters.name}
            onChange={(e) => setFilters({...filters, name: e.target.value})}
          />
          <input
            type="text"
            placeholder="Zoek op email..."
            value={filters.email}
            onChange={(e) => setFilters({...filters, email: e.target.value})}
          />
          <select
  value={filters.hasActivePackage}
  onChange={(e) => setFilters({...filters, hasActivePackage: e.target.value})}
>
  <option value="">Alle gebruikers</option>
  <option value="active">Met actieve packages</option>
  <option value="inactive">Met inactieve packages</option>
  <option value="none">Zonder packages</option>
</select>
          <button onClick={applyFilters} className="btn btn-outline">
            Filteren
          </button>
        </div>
      </div>

      <div className="users-table">
        {loading ? (
          <div className="loading">Laden...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Naam</th>
                <th>Email</th>
                <th>Totaal Scans</th>
                <th>Gebruikt</th>
                <th>Beschikbaar</th>
                <th>Vervaldatum</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr 
                  key={user.email} 
                  onClick={() => openUserModal(user)}
                  className="user-row-clickable"
                >
                  <td>{user.name || '-'}</td>
                  <td>{user.email}</td>
                  <td>{user.packageStatus.scansTotal || 0}</td>
                  <td>{user.packageStatus.scansUsed || 0}</td>
                  <td>
                    <strong>{user.packageStatus.scansLeft || 0}</strong>
                  </td>
                  <td>
                    {user.packageExpiresAt ? 
                      new Date(user.packageExpiresAt.seconds * 1000).toLocaleDateString('nl-NL') : 
                      '-'
                    }
                  </td>
                  <td>
                    <span className={`status-badge ${user.hasActivePackage ? 'active' : 'inactive'}`}>
                      {user.hasActivePackage ? 'Actief' : 'Inactief'}
                    </span>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
                    Geen gebruikers gevonden
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderActivation = () => (
    <div className="package-activation">
      <h3>Package Activeren</h3>
      <form onSubmit={handleActivatePackage} className="activation-form">
        <div className="form-row">
          <div className="form-group">
            <label>Naam (optioneel)</label>
            <input
              type="text"
              value={activationForm.name}
              onChange={(e) => setActivationForm({...activationForm, name: e.target.value})}
              placeholder="Jan Jansen"
            />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={activationForm.email}
              onChange={(e) => setActivationForm({...activationForm, email: e.target.value})}
              placeholder="jan@bedrijf.nl"
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Aantal Scans *</label>
            <select
              value={activationForm.scansTotal}
              onChange={(e) => setActivationForm({...activationForm, scansTotal: parseInt(e.target.value)})}
              required
            >
              <option value={1}>1 Scan</option>
              <option value={2}>2 Scans</option>
              <option value={4}>4 Scans (Standaard)</option>
              <option value={8}>8 Scans</option>
              <option value={12}>12 Scans</option>
              <option value={20}>20 Scans</option>
            </select>
          </div>
          <div className="form-group">
            <label>Geldigheid (dagen) *</label>
            <select
              value={activationForm.validityDays}
              onChange={(e) => setActivationForm({...activationForm, validityDays: parseInt(e.target.value)})}
              required
            >
              <option value={30}>30 dagen</option>
              <option value={90}>90 dagen</option>
              <option value={180}>180 dagen</option>
              <option value={365}>365 dagen (Standaard)</option>
              <option value={730}>730 dagen (2 jaar)</option>
            </select>
          </div>
        </div>
        
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Activeren...' : 'Package Activeren'}
        </button>
      </form>
    </div>
  );

  const renderExtension = () => (
    <div className="package-extension">
      <h3>Package Verlengen</h3>
      <form onSubmit={handleExtendPackage} className="extension-form">
        <div className="form-row">
          <div className="form-group">
            <label>User Email *</label>
            <input
              type="email"
              value={extensionForm.userEmail}
              onChange={(e) => setExtensionForm({...extensionForm, userEmail: e.target.value})}
              placeholder="user@bedrijf.nl"
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Extra Scans</label>
            <input
              type="number"
              min="0"
              value={extensionForm.additionalScans}
              onChange={(e) => setExtensionForm({...extensionForm, additionalScans: parseInt(e.target.value) || 0})}
            />
          </div>
          <div className="form-group">
            <label>Extra Dagen</label>
            <input
              type="number"
              min="0"
              value={extensionForm.additionalDays}
              onChange={(e) => setExtensionForm({...extensionForm, additionalDays: parseInt(e.target.value) || 0})}
            />
          </div>
        </div>
        
        <button type="submit" className="btn btn-secondary" disabled={loading}>
          {loading ? 'Verlengen...' : 'Package Verlengen'}
        </button>
      </form>
    </div>
  );

  // NEW: User detail modal
  const renderUserModal = () => {
    if (!showUserModal || !selectedUser) return null;

    return (
      <>
        <div className="modal-overlay" onClick={closeUserModal}></div>
        <div className="user-modal">
          <div className="modal-header">
            <h3>Gebruiker Beheren</h3>
            <button className="modal-close" onClick={closeUserModal}>×</button>
          </div>
          
          <div className="modal-body">
            <div className="user-info-section">
              <div className="user-info-row">
                <span className="user-info-label">Naam:</span>
                <span className="user-info-value">{selectedUser.name || '-'}</span>
              </div>
              <div className="user-info-row">
                <span className="user-info-label">Email:</span>
                <span className="user-info-value">{selectedUser.email}</span>
              </div>
              <div className="user-info-row">
                <span className="user-info-label">Status:</span>
                <span className={`status-badge ${selectedUser.hasActivePackage ? 'active' : 'inactive'}`}>
                  {selectedUser.hasActivePackage ? 'Actief' : 'Inactief'}
                </span>
              </div>
            </div>

            {selectedUser.packageStatus.hasPackage && (
              <div className="package-info-section">
                <h4>Package Informatie</h4>
                <div className="package-stats">
                  <div className="package-stat">
                    <span className="package-stat-label">Totaal Scans</span>
                    <span className="package-stat-value">{selectedUser.packageStatus.scansTotal}</span>
                  </div>
                  <div className="package-stat">
                    <span className="package-stat-label">Gebruikt</span>
                    <span className="package-stat-value">{selectedUser.packageStatus.scansUsed}</span>
                  </div>
                  <div className="package-stat">
                    <span className="package-stat-label">Beschikbaar</span>
                    <span className="package-stat-value highlight">{selectedUser.packageStatus.scansLeft}</span>
                  </div>
                  <div className="package-stat">
                    <span className="package-stat-label">Dagen Over</span>
                    <span className="package-stat-value">{selectedUser.packageStatus.daysLeft}</span>
                  </div>
                </div>
                
                {selectedUser.packageExpiresAt && (
                  <div className="expiry-info">
                    <span>Verloopt op: </span>
                    <strong>{new Date(selectedUser.packageExpiresAt.seconds * 1000).toLocaleDateString('nl-NL')}</strong>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <h4>Snelle Acties</h4>
              
              <div className="quick-actions">
                <button 
                  onClick={() => handleQuickAddScans(1)}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  + 1 Scan
                </button>
                <button 
                  onClick={() => handleQuickAddScans(4)}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  + 4 Scans
                </button>
                <button 
                  onClick={() => handleQuickExtendValidity(90)}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  + 90 Dagen
                </button>
                <button 
                  onClick={() => handleQuickExtendValidity(365)}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  + 365 Dagen
                </button>
              </div>

              <div className="advanced-actions">
                <button 
                  onClick={() => {
                    setExtensionForm({...extensionForm, userEmail: selectedUser.email});
                    setActiveTab('extension');
                    closeUserModal();
                  }}
                  className="btn btn-primary"
                >
                  Geavanceerd Verlengen
                </button>
                
                {selectedUser.hasActivePackage && (
                  <button 
                    onClick={() => handleDeactivatePackage(selectedUser.email)}
                    className="btn btn-danger"
                    disabled={loading}
                  >
                    Package Deactiveren
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="package-management">
      <div className="package-header">
        <h2>Package Management</h2>
        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.text}
          </div>
        )}
      </div>

      {/* UPDATED: Tab volgorde aangepast */}
      <div className="package-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overzicht
        </button>
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Gebruikers
        </button>
        <button 
          className={`tab ${activeTab === 'activation' ? 'active' : ''}`}
          onClick={() => setActiveTab('activation')}
        >
          Activeren
        </button>
        <button 
          className={`tab ${activeTab === 'extension' ? 'active' : ''}`}
          onClick={() => setActiveTab('extension')}
        >
          Verlengen
        </button>
      </div>

      <div className="package-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'activation' && renderActivation()}
        {activeTab === 'extension' && renderExtension()}
      </div>

      {/* NEW: User modal */}
      {renderUserModal()}
    </div>
  );
};

export default PackageManagement;